import { useEffect } from "react";
/**
 * S6 F-03 — idle-gated FPS sampler + adaptive-controller defaults.
 *
 * Extracted from `useWidgetData.ts` (FPS rAF loop). The old loop ran
 * `requestAnimationFrame` unconditionally — every frame, forever, even on a
 * fully idle chart. The sampler below sleeps after `idleTimeoutMs` without
 * activity (live tick / backfill / zoom) and re-checks via timeout instead
 * of rAF, so an idle multi-chart workspace stops waking the CPU.
 */
export const IDLE_FPS_SLEEP_MS = 2000;
/** Cold-start / backfill-safe controller state — mirrors the ref inits in `useWidgetData`. */
export const DEFAULT_ADAPTIVE_CONTROLLER_STATE = {
    maxInflight: 2,
    debounceMs: 120,
    chunkBars: 5000,
    retentionFactor: 3.0,
    highFrameTimeCount: 0,
    lowFrameTimeCount: 0,
    highOverfetchCount: 0,
};
/**
 * FPS sampler that sleeps while idle. Sampling semantics are identical to
 * the loop it replaces (delta < 200ms kept, cap 300 samples); only the
 * scheduling is activity-gated.
 */
export function useIdleFpsSampler({ disabled, frameTimesRef, lastFrameTimeRef, getLastActivityMs, idleTimeoutMs = IDLE_FPS_SLEEP_MS, }) {
    useEffect(() => {
        // rAF/timeout are ambient browser scope (same class as monacoPaths.ts /
        // useLicenseDiagnostics.ts): no adapter exists to inject, and the
        // `disabled` flag already covers test/SSR environments.
        /* eslint-disable-next-line no-restricted-globals -- ambient rAF/timeout scope, no injectable adapter; guarded by `disabled` */
        if (disabled || typeof window === "undefined") {
            return undefined;
        }
        let rafId = 0;
        let sleepTimer;
        let disposed = false;
        const sample = (now) => {
            if (lastFrameTimeRef.current !== null) {
                const delta = now - lastFrameTimeRef.current;
                if (delta < 200) {
                    frameTimesRef.current.push(delta);
                    if (frameTimesRef.current.length > 300) {
                        frameTimesRef.current.shift();
                    }
                }
            }
            lastFrameTimeRef.current = now;
        };
        const step = (now) => {
            if (disposed)
                return;
            if (now - getLastActivityMs() > idleTimeoutMs) {
                // Idle: sleep — re-check via timeout instead of burning rAF.
                /* eslint-disable-next-line no-restricted-globals -- ambient timeout scope, see guard above */
                sleepTimer = window.setTimeout(() => {
                    if (!disposed)
                        rafId = requestAnimationFrame(step);
                }, idleTimeoutMs);
                return;
            }
            sample(now);
            rafId = requestAnimationFrame(step);
        };
        rafId = requestAnimationFrame(step);
        return () => {
            disposed = true;
            cancelAnimationFrame(rafId);
            /* eslint-disable-next-line no-restricted-globals -- ambient timeout scope, see guard above */
            if (sleepTimer !== undefined)
                window.clearTimeout(sleepTimer);
        };
        // Refs are stable containers; the activity getter is read fresh each frame.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, idleTimeoutMs]);
}
